/* eslint-disable no-unused-vars */
// src/hooks/useAvailableJobNotifications.js
import { useEffect } from 'react';
import { io } from 'socket.io-client';
import { toast } from 'react-toastify';
import audioNotificationService from '../services/audioNotificationService';

// Helper function to format date/time like My Diary
const formatWhen = (iso) => {
  try {
    const date = new Date(iso);
    if (isNaN(date.getTime())) {
      console.warn('Invalid date in formatWhen:', iso);
      return 'Invalid Date';
    }
    const parts = new Intl.DateTimeFormat('en-GB', {
      hour: 'numeric', minute: '2-digit', hour12: true,
      weekday: 'short', day: 'numeric', month: 'short',
      timeZone: 'Europe/London'
    }).formatToParts(date);
    const p = Object.fromEntries(parts.map(x => [x.type, x.value]));
    return `${p.hour}:${p.minute} ${p.dayPeriod?.toLowerCase()}, ${p.weekday} ${p.day} ${p.month}`;
  } catch (error) {
    console.warn('Error formatting date:', iso, error);
    return 'Invalid Date';
  }
};

// Helper function to show flash notification for available jobs
const showAvailableJobFlashNotification = (urgency = 'normal') => {
  console.log(`🎬 Creating flash notification for available job (urgency: ${urgency})`);
  
  const isUrgent = urgency === 'urgent';
  
  // Create a flash overlay
  const flashOverlay = document.createElement('div');
  flashOverlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: ${isUrgent ? 'rgba(220, 53, 69, 0.9)' : 'rgba(0, 123, 255, 0.8)'};
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: ${isUrgent ? 'urgentJobFlash' : 'availableJobFlash'} 0.5s ease-in-out;
  `;
  
  // Add flash animation
  const style = document.createElement('style');
  style.textContent = `
    @keyframes availableJobFlash {
      0%, 100% { opacity: 0; }
      50% { opacity: 1; }
    }
    @keyframes urgentJobFlash {
      0%, 100% { opacity: 0; transform: scale(0.95); }
      50% { opacity: 1; transform: scale(1.05); }
    }
  `;
  document.head.appendChild(style);
  
  // Add text
  const text = document.createElement('div');
  text.textContent = isUrgent ? '🚨 URGENT: New Available Job!' : '🚖 New Available Job!';
  text.style.cssText = `
    color: white;
    font-size: ${isUrgent ? '28px' : '24px'};
    font-weight: bold;
    text-align: center;
    text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
    animation: ${isUrgent ? 'pulseText 1s infinite' : 'none'};
  `;
  flashOverlay.appendChild(text);
  
  // Add pulse animation for urgent jobs
  if (isUrgent) {
    const pulseStyle = document.createElement('style');
    pulseStyle.textContent = `
      @keyframes pulseText {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.1); }
      }
    `;
    document.head.appendChild(pulseStyle);
    setTimeout(() => document.head.removeChild(pulseStyle), 3000);
  }
  
  document.body.appendChild(flashOverlay);
  
  // Remove after animation (longer for urgent)
  setTimeout(() => {
    if (document.body.contains(flashOverlay)) {
      document.body.removeChild(flashOverlay);
    }
    if (document.head.contains(style)) {
      document.head.removeChild(style);
    }
  }, isUrgent ? 3000 : 2000);
};

// Helper function to play available job sound
const playAvailableJobSound = async (urgency = 'normal') => {
  console.log(`🔊 Playing available job sound with voice mapping support (urgency: ${urgency})`);
  
  try {
    // Use different audio for ASAP/urgent jobs if available
    if (urgency === 'asap') {
      // Try to play urgent sound, fallback to pool sound
      try {
        await audioNotificationService.playNewJobAudio('asap');
      } catch (e) {
        // Fallback to pool sound if asap not available
        await audioNotificationService.playNewJobAudio('pool');
      }
    } else {
      // Use the audio mapping service for pool job notifications
      await audioNotificationService.playNewJobAudio('pool');
    }
  } catch (error) {
    console.error('Audio notification failed:', error);
  }
};

export default function useAvailableJobNotifications() {
  useEffect(() => {
    const myId = localStorage.getItem('userId');
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const socket = io(baseUrl, {
      // Allow polling fallback to avoid early close during upgrade on some hosts
      transports: ['websocket', 'polling'],
      path: '/socket.io',
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelayMax: 5000
    });

    socket.on('connect', () => console.log('🔌 Available Job Socket connected:', socket.id));
    socket.on('disconnect', () => console.log('❌ Available Job Socket disconnected:', socket.id));
    socket.on('connect_error', (err) => {
      console.warn('Available Job Socket connect_error:', err?.message || err);
    });

    // New available job in monitored areas
    socket.on('new-available-job', data => {
      console.log('🔔 Received new-available-job event:', data);
      
      const isASAP = data.isASAP || false;
      
      // Show flash notification (enhanced for ASAP jobs)
      if (isASAP) {
        showAvailableJobFlashNotification('urgent');
      } else {
        showAvailableJobFlashNotification();
      }
      
      // Play audio sound (different for ASAP)
      if (isASAP) {
        playAvailableJobSound('asap');
      } else {
        playAvailableJobSound();
      }
      
      // Show toast notification with timing and location details
      const timingInfo = isASAP ? 
        '⚡ ASAP - URGENT' : 
        (data.dateTime ? `📅 ${formatWhen(data.dateTime)}` : '⚡ ASAP');
      
      const customerInfo = data.customerName ? `👤 ${data.customerName}` : '';
      const passengerInfo = data.passengers ? `👥 ${data.passengers} passenger${data.passengers > 1 ? 's' : ''}` : '';
      
      // Enhanced styling for ASAP jobs
      const notificationStyle = isASAP ? {
        background: '#dc3545', // Red for urgent
        color: 'white',
        fontSize: '18px',
        fontWeight: 'bold',
        whiteSpace: 'pre-line',
        animation: 'pulse 2s infinite',
        border: '3px solid #ffc107'
      } : {
        background: '#007bff',
        color: 'white',
        fontSize: '16px',
        fontWeight: 'bold',
        whiteSpace: 'pre-line'
      };
      
      toast.info(
        `${isASAP ? '🚨 URGENT: ' : ''}🚖 New Pool Job\n${timingInfo}\n📍 ${data.from} → ${data.to}${customerInfo ? `\n${customerInfo}` : ''}${passengerInfo ? `\n${passengerInfo}` : ''}`, 
        {
          autoClose: isASAP ? 10000 : false, // Auto-close ASAP jobs after 10s to avoid clutter
          closeOnClick: false,
          draggable: false,
          style: notificationStyle
        }
      );
      
      // Emit custom event to refresh available jobs data
      window.dispatchEvent(new CustomEvent('refreshAvailableJobs', {
        detail: { 
          leadId: data.leadId, 
          from: data.from, 
          to: data.to,
          postcode: data.postcode,
          monitoredAreas: data.monitoredAreas,
          isASAP: isASAP
        }
      }));
    });

    return () => socket.disconnect();
  }, []);
}
